import React, { useCallback, useMemo, useState } from 'react';
import Header from '../../../components/common/global-top-bar';
import { Paginator } from '../../../components/common/paginator';
import AudioPlayer from '../../../components/common/audio-player';
import { EmptyState, TagList } from '../components/common';
import { formatDateTime } from '../utils/collection-helpers';
import {
  useAudioSamplesList,
  useCreateAudioSample,
  useDeleteAudioSample,
  type AudioSample,
} from '../../../hooks/useAudioSamples';
import { use2026TagGroups } from '../../../hooks/useTagGroups';
import { useTagMap } from '../utils/tag-helpers';
import { AddAudioSampleModal } from './add-audio-sample-modal';

const PAGE_LIMIT = 20;

type ContentTab = 'audio' | 'video';

const tabs: { key: ContentTab; label: string }[] = [
  { key: 'audio', label: 'Audio' },
  { key: 'video', label: 'Video' },
];

const AudioSamplesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ContentTab>('audio');
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);

  const { data, isLoading } = useAudioSamplesList(
    { page, limit: PAGE_LIMIT, experience_type: activeTab },
    { enabled: true },
  );

  const { mutateAsync: createSample, isLoading: isCreating } = useCreateAudioSample();
  const { mutateAsync: deleteSample, isLoading: isDeleting } = useDeleteAudioSample();
  const { allTags } = use2026TagGroups();
  const tagMap = useTagMap(allTags);

  const samples = data?.data ?? [];
  const count = data?.count ?? 0;

  const existingPairs = useMemo(
    () => samples.map((s) => ({ vertical_id: s.vertical_id, listener_audio_id: s.listener_audio_id })),
    [samples],
  );

  const handleAddSelect = useCallback(
    async (verticalId: number, listenerAudioId: number) => {
      await createSample({ vertical_id: verticalId, listener_audio_id: listenerAudioId });
      setShowAddModal(false);
    },
    [createSample],
  );

  const handleRemove = useCallback(
    async (sample: AudioSample) => {
      await deleteSample(sample.id);
    },
    [deleteSample],
  );

  const isUpdating = isCreating || isDeleting;

  const handleTabChange = (tab: ContentTab) => {
    setActiveTab(tab);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div>
        <Header heading="PWA Samples" />
        <div className="flex justify-center px-6 py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header heading="PWA Samples" />
      <div className="flex flex-col gap-4 px-6 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">
          PWA samples by vertical. Add listener audio or video to feature for a vertical.
        </p>
        <button
          type="button"
          className="bg-blue-primary h-10 rounded-md px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setShowAddModal(true)}
          disabled={isUpdating}
        >
          Add Sample
        </button>
      </div>
      <div className="px-6 py-6">
        <div className="mb-4 flex border-b border-gray-200">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                className={`mr-4 border-b-2 px-4 py-2 text-sm font-semibold ${
                  isActive ? 'border-blue-primary text-blue-primary' : 'border-transparent text-gray-500'
                }`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        {!samples.length ? (
          <EmptyState
            message={`No ${activeTab} samples yet. Click Add Sample to add listener ${activeTab} for a vertical.`}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead>
                <tr className="bg-gray-100 text-left text-xs uppercase text-gray-500">
                  {activeTab === 'audio' && <th className="w-32 px-4 py-3">Play</th>}
                  {activeTab === 'video' && <th className="w-32 px-4 py-3">Video</th>}
                  <th className="w-16 px-4 py-3">Image</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Listener</th>
                  <th className="w-24 px-4 py-3">Role ID</th>
                  <th className="px-4 py-3">Vertical</th>
                  <th className="px-4 py-3">Tags</th>
                  <th className="w-40 px-4 py-3">Added</th>
                  <th className="w-32 px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {samples.map((sample) => {
                  const la = sample.listener_audio;
                  const isVideo = la?.media_type === 'VIDEO';
                  const listenerName = la?.display_name ?? `Role ${la?.listener_role_id ?? sample.listener_audio_id}`;
                  const profilePhoto = la?.profile_photo_square_file_url;
                  const tagNames = (() => {
                    if (la?.tag_names?.length) return la.tag_names;
                    const ids = la?.topic_tag_ids ?? [];
                    if (tagMap && ids.length) {
                      return ids
                        .map((id) => tagMap.get(id)?.name)
                        .filter((name): name is string => typeof name === 'string' && name.length > 0);
                    }
                    return [];
                  })();
                  const titleLabel = isVideo ? 'Video' : 'Audio';
                  return (
                    <tr key={sample.id} className="border-b border-gray-100 text-sm text-gray-700">
                      {activeTab === 'audio' && (
                        <td className="px-4 py-3">
                          {la?.file_url != null ? (
                            <AudioPlayer url={la.file_url} isTimeDisplay audioDuration={la.duration ?? 0} />
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      )}
                      {activeTab === 'video' && (
                        <td className="px-4 py-3">
                          {la?.video_id ? (
                            <a
                              href={`https://vimeo.com/${la.video_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-primary text-xs font-semibold"
                            >
                              Watch
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        {profilePhoto ? (
                          <img src={profilePhoto} alt={listenerName} className="h-10 w-10 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-xs text-gray-500">
                            —
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{la?.title ?? `${titleLabel} ${sample.listener_audio_id}`}</div>
                        <div className="text-xs text-gray-500">
                          #{sample.listener_audio_id}
                          {isVideo && la?.video_id && (
                            <>
                              {' · '}
                              <a
                                href={`https://vimeo.com/${la.video_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-primary"
                              >
                                Watch
                              </a>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">{listenerName}</td>
                      <td className="px-4 py-3 font-mono text-xs">{la?.listener_role_id ?? '—'}</td>
                      <td className="px-4 py-3">{sample.vertical?.name ?? `Vertical ${sample.vertical_id}`}</td>
                      <td className="px-4 py-3">
                        <TagList tags={tagNames} />
                      </td>
                      <td className="px-4 py-3 text-gray-500">{formatDateTime(sample.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          className="rounded-md border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => handleRemove(sample)}
                          disabled={isUpdating}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {count > PAGE_LIMIT && (
          <div className="mt-4 flex justify-end">
            <Paginator count={count} limit={PAGE_LIMIT} currentPage={page} handlePageChange={setPage} />
          </div>
        )}
      </div>
      <AddAudioSampleModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSelect={handleAddSelect}
        existingPairs={existingPairs}
        isBusy={isCreating}
        experienceType={activeTab}
      />
    </div>
  );
};

export default AudioSamplesPage;
